import axios from 'axios'
import moment from 'moment'
import { set } from 'lodash'
import { slug, isLikelyAuMobile } from '@shared/Utilities'
import swal from 'sweetalert'

import { postdata } from '@shared/Utilities'
import router from '@builder-web/Routes'
import random from 'random-words'

const SET_JOBS = 'SET_JOBS'
const SET_JOB_FORM = 'SET_JOB_FORM'
const ADD_JOB = 'ADD_JOB'
const INPUT_CHANGED = 'INPUT_CHANGED'
const PRODUCT_TAB_CHANGED = 'PRODUCT_TAB_CHANGED'
const PAGE_CHANGED = 'PAGE_CHANGED'
const CHECKBOX_CHANGED = 'CHECKBOX_CHANGED'
const RADIO_CHANGED = 'RADIO_CHANGED'
const CLEAR_FORM = 'CLEAR_FORM'
const CLEAR_ATTACHMENTS = 'CLEAR_ATTACHMENTS'
const UPDATE_ATTACHMENTS = 'UPDATE_ATTACHMENTS'
const SET_FORM_ERRORS = 'SET_FORM_ERRORS'
const JOB_FORM_REQUESTING = 'JOB_FORM_REQUESTING'
const SET_FORM_JOB_SUPPLIER = 'SET_FORM_JOB_SUPPLIER'
const CLEAR_FORM_ERRORS = 'CLEAR_FORM_ERRORS'
const ADD_FORM_PRODUCT = 'ADD_FORM_PRODUCT'
const SET_ACTIVE_PRODUCT_INDEX = 'SET_ACTIVE_PRODUCT_INDEX'
const REMOVE_FORM_PRODUCT = 'REMOVE_FORM_PRODUCT'
const SET_REQUESTS = 'SET_REQUESTS'
const SET_DRAGGABLE_FLAG = 'SET_DRAGGABLE_FLAG'
const CLEAR_SELECTED_PRODUCT = 'CLEAR_SELECTED_PRODUCT'
const SET_EDIT_MODE = 'SET_EDIT_MODE'
const SET_DISPLAY_ADDRESS_MAP_FLAG = 'SET_DISPLAY_ADDRESS_MAP_FLAG'
const SET_JOB_COUNT = 'SET_JOB_COUNT'
const SET_TOTAL_JOB_COUNT = 'SET_TOTAL_JOB_COUNT'
const RESET_STATE = 'RESET_STATE'

const jobForm = {
    page: null,
    productTab: null,
    selectedProduct: [],
    productIndex: 0,
    form: {
        products: [],
        schedule_time: null,
        schedule_date: null,
        site_restriction: null,
        truck_size: null,
        transport_method: null,
        logistics: null,
        address: null,
        city: null,
        state: null,
        postcode: null,
        additionals: null,
        request_to: null,
        purchase_order: null,
        number: null,
        order_confirmed: false,
        supply_time_window: null,
        supplier_address: null,
        additionals: null,
        site_contact_name: null,
        site_contact_phone: null,
        site_instructions: null,
        attachments: [],
        original_file_names: [],
        encrypted_file_names: [],
        geometry: { lat: null, lng: null },
    },
    errors: {},
    processing: false,
    loading: false,
    dragLocation: false,
    displayMapAddress: false,
}

/**
 * Vuex state for job module
 * @type {Object}
 */
const getDefaultState = () => ({
    jobs: [],
    count: 0,
    jobCount: 0,
    jobForm: { ...jobForm },
    requests: [],
    bidcrete_phone_number: '+61 1300 820 578',
    bidcrete_email_address: 'info@bidcrete.com.au',
})

const state = getDefaultState()

/**
 * Vuex mutations for job module
 * @type {Object}
 */
const mutations = {
    [SET_JOB_COUNT]: (state, payload) => {
        state.jobCount = payload
    },

    [SET_TOTAL_JOB_COUNT]: (state, payload) => {
        state.count = payload
    },

    [SET_JOBS]: (state, payload) => {
        state.jobs = payload
    },

    [SET_JOB_FORM]: (state, { payload, editMode }) => {
        const {
            products,
            requested_to,
            schedule,
            site_restriction,
            truck_size,
            transport_method,
            logistics,
            address,
            purchase_order,
            number,
            geometry,
            orderedSteel,
            attachments,
            supply_time_window,
            supplier_address,
            additionals,
            site_contact_name,
            site_contact_phone,
            site_instructions,
        } = payload

        state.jobForm.form = {
            products,
            site_restriction,
            truck_size,
            transport_method,
            logistics,
            purchase_order,
            number,
            geometry,
            orderedSteel,
            additionals,
            site_contact_name,
            site_contact_phone,
            site_instructions,
            address: address.long_address,
            state: address.state,
            city: address.city,
            postcode: address.postcode,
            stateCode: address.statecode,
            schedule_time: moment(schedule).format('h:mm A'),
            schedule_date: moment(schedule).format('DD/MM/YYYY'),
        }

        if (requested_to) state.jobForm.form.request_to = requested_to.id

        if (editMode) {
            products.forEach((product, index) => {
                if (!product.is_concrete) {
                    const { type } = product
                    const segregatedTypes = type.split('-')

                    state.jobForm.form.products[index].type = segregatedTypes[0]
                    state.jobForm.form.products[index].subtype =
                        segregatedTypes[1]
                }

                if (product.attachments) {
                    state.jobForm.form.products[index].original_file_names = []
                    product.attachments.forEach((attachment) => {
                        state.jobForm.form.products[
                            index
                        ].original_file_names.push(attachment.name)
                    })
                    state.jobForm.form.products[index].attachments = []
                }
            })

            // For job attachments
            if (attachments) {
                state.jobForm.form.original_file_names = []
                attachments.forEach((attachment) => {
                    state.jobForm.form.original_file_names.push(attachment.name)
                })
                state.jobForm.form.attachments = []
            }

            state.jobForm.form.supplier_address = supplier_address?.id || null
            state.jobForm.form.supply_time_window = supply_time_window?.id || null
        }
    },

    [SET_REQUESTS]: (state, payload) => {
        state.requests = payload
    },

    [ADD_JOB]: (state, payload) => {
        state.jobs.push(payload)
    },

    [SET_FORM_JOB_SUPPLIER]: (state, payload) => {
        state.jobForm.form.request_to = payload
    },

    [INPUT_CHANGED]: (state, payload) => {
        const { key, value, subkey, subvalue, field, fieldvalue } = payload
        const stack = Object.assign({}, state.jobForm.form)

        if (key) {
            set(stack, key, value)
        }

        if (field) {
            set(stack, field, fieldvalue)
        }

        if (subkey) {
            set(stack, subkey, subvalue)
        }

        state.jobForm.form = stack
    },

    [CHECKBOX_CHANGED]: (state, { key, value }) => {
        const index = state.jobForm.form[key].findIndex(
            (e) => e.id === value.id,
        )

        if (index === -1) {
            state.jobForm.form[key].push(value)
        } else {
            state.jobForm.form[key].splice(index, 1)
        }
    },

    [RADIO_CHANGED]: (state, payload) => {
        state.jobForm.selectedProduct = payload
    },

    [PRODUCT_TAB_CHANGED]: (state, key) => {
        state.jobForm.productTab = key
    },

    [PAGE_CHANGED]: (state, page) => {
        state.jobForm.page = page
    },

    [CLEAR_FORM]: (state) => {
        state.jobForm = { ...jobForm }
        state.jobForm.form.products = []
        state.jobForm.form.request_to = null
    },

    [CLEAR_ATTACHMENTS]: (state, key) => {
        state.jobForm.form.products[key].attachments = []
        state.jobForm.form.products[key].file_names = []
        state.jobForm.form.products[key].original_file_names = []
    },

    [CLEAR_FORM_ERRORS]: (state) => {
        state.jobForm.errors = {}
    },

    [UPDATE_ATTACHMENTS]: (state, payload) => {
        let index = state.jobForm.productIndex

        state.jobForm.form.products[index]['attachments'] = []
        state.jobForm.form.products[index]['original_file_names'] = []
        state.jobForm.form.products[index]['file_names'] = []

        payload.forEach(
            function (file) {
                file['attachments'].forEach(
                    function (attachment) {
                        state.jobForm.form.products[index]['file_names'].push(
                            attachment['encrypted'],
                        )
                        state.jobForm.form.products[index][
                            'original_file_names'
                        ].push(attachment['original'])
                    }.bind(this),
                )
            }.bind(this),
        )
    },

    [SET_FORM_ERRORS]: (state, payload) => {
        state.jobForm.errors = payload
    },

    [JOB_FORM_REQUESTING]: (state, payload) => {
        state.jobForm.loading = payload
    },

    [SET_ACTIVE_PRODUCT_INDEX]: (state, payload) => {
        state.jobForm.productIndex = payload
    },

    [ADD_FORM_PRODUCT]: (state, payload) => {
        const withCategory = {
            ...payload,
            category: payload.category_id || payload.id,
        }
        const productIndex = state.jobForm.productIndex + 1
        state.jobForm.form.products.splice(productIndex, 0, withCategory)
    },

    [REMOVE_FORM_PRODUCT]: (state, key) => {
        state.jobForm.form.products.splice(key, 1)
    },

    [SET_DRAGGABLE_FLAG]: (state, payload) => {
        state.jobForm.dragLocation = payload
    },

    [SET_DISPLAY_ADDRESS_MAP_FLAG]: (state, payload) => {
        state.jobForm.displayMapAddress = payload
    },

    [CLEAR_SELECTED_PRODUCT]: (state) => {
        state.jobForm.selectedProduct = []
    },

    [RESET_STATE]: (state) => {
        Object.assign(state, getDefaultState())
    },
}

/**
 * Vuex actions for job module
 * @type {Object}
 */
const actions = {
    async fetchJobRequests({ commit, dispatch }) {
        dispatch('wait/start', 'job.fetchJobRequests', { root: true })
        const { data } = await axios.get('/api/job-requests')
        commit(SET_REQUESTS, data.data)
        dispatch('wait/end', 'job.fetchJobRequests', { root: true })

        return data.data
    },

    async fetchJobs({ dispatch }, payload) {
        dispatch('wait/start', 'job.fetchJobs', { root: true })
        let endpoint = '/api/jobs'
        let queryParams = {}

        if (payload) {
            queryParams = {
                params: payload,
            }
        }

        const { data } = await axios.get(endpoint, queryParams)

        dispatch('wait/end', 'job.fetchJobs', { root: true })
        dispatch('setJobCount', data.recordsTotal || 0)
        dispatch('setTotalJobCount', data.count || 0)
        dispatch('setJobs', data.data)

        return data
    },

    async fetchJob({ dispatch }, { uuid, editMode }) {
        dispatch('wait/start', 'job.fetchJob', { root: true })

        const { data } = await axios.get(`/api/jobs/${uuid}`)
        if (data) {
            dispatch('setJobForm', {
                payload: data.data,
                editMode,
            })
        }

        dispatch('wait/end', 'job.fetchJob', { root: true })
        return data.data
    },

    async fetchUpcomingJobs({ dispatch }, payload) {
        dispatch('wait/start', 'job.fetchUpcomingJobs', { root: true })

        const { data } = await axios.get('/api/jobs', {
            params: {
                status: 'ongoing',
                ...payload,
            },
        })

        dispatch('wait/end', 'job.fetchUpcomingJobs', { root: true })
        return data.data
    },

    async fetchRecentJobs(
        { dispatch },
        {
            limit,
            minimal = 1,
            hideCancelledJobs = 0,
            sortByBids = 0,
            count = 0,
        },
    ) {
        dispatch('wait/start', 'job.fetchRecentJobs', { root: true })
        const { data } = await axios.get(`/api/jobs`, {
            params: {
                limit: limit || 5,
                minimal,
                count,
                hide_cancelled_jobs: hideCancelledJobs ? 1 : 0,
                sort_by_bids: sortByBids ? 1 : 0,
            },
        })
        dispatch('wait/end', 'job.fetchRecentJobs', { root: true })

        return data.data
    },

    setRequests({ commit }, requests) {
        commit(SET_REQUESTS, requests)
    },

    setJobs({ commit }, jobs) {
        commit(SET_JOBS, jobs)
    },

    setJobCount({ commit }, count) {
        commit(SET_JOB_COUNT, count)
    },

    setTotalJobCount({ commit }, count) {
        commit(SET_TOTAL_JOB_COUNT, count)
    },

    addJob({ commit }, job) {
        commit(ADD_JOB, job)
    },

    async submitForm({ commit, state }, { onSuccess, uuid, onFailed }) {
        const { jobForm } = state
        const endpoint = uuid ? `/api/jobs/${uuid}` : `/api/jobs`

        if (jobForm.form.order_confirmed) {
            try {
                commit(JOB_FORM_REQUESTING, true)

                const form = Object.assign({}, jobForm.form)
                const orderedSteel = form.orderedSteel
                const encryptedFileNames = form.encrypted_file_names

                delete form.steelCategory
                delete form.selectedCategory
                delete form.orderedSteel
                delete form.original_file_names
                delete form.encrypted_file_names
                delete form.attachments

                form.attachments = encryptedFileNames

                const { data } = await axios.post(
                    endpoint,
                    postdata({
                        ...form,
                        uuid: random({ exactly: 4, maxLength: 5 }).join(''),
                        schedule: [
                            moment(
                                jobForm.form.schedule_date,
                                'DD/MM/YYYY',
                            ).format('MM/DD/YYYY'),
                            jobForm.form.schedule_time,
                        ].join(' '),
                        products: jobForm.form.products
                            .filter(product => orderedSteel ? product.quantity > 0 : true)
                            .map((e) => ({
                                id: e.id,
                                type: e.type,
                                subtype: e.subtype,
                                strength: e.strength,
                                aggregate_size: e.aggregate_size,
                                slump: e.slump,
                                attachments: uuid
                                    ? e.original_file_names || e.file_names
                                    : e.file_names,
                                volume: e.volume,
                                quantity: e.quantity,
                                price: e.price,
                                specifications: e.specifications,
                                truck_spacing:
                                    e.truck_spacing == 'None'
                                        ? ''
                                        : e.truck_spacing,
                                additionals: e.additionals,
                                note: e.note,
                                add_plus_load: e.add_plus_load,
                                pump_name: e.pump_name,
                                concrete_name: e.concrete_name,
                                concrete_color: e.concrete_color,
                                testing_required: e.testing_required,
                                site_instructor_name: e.site_instructor_name,
                                site_instructor_phone: e.site_instructor_phone,
                            }
                        )),
                    }),
                )

                commit(JOB_FORM_REQUESTING, false)

                if (onSuccess instanceof Function) await onSuccess(data)
            } catch (e) {
                commit(JOB_FORM_REQUESTING, false)

                if (e.response?.status === 422) {
                    commit(SET_FORM_ERRORS, e.response.data.errors)
                }

                if (onFailed instanceof Function) await onFailed(e)
            }
        } else {
            if (onFailed instanceof Function)
                await onFailed({
                    title: "We Can't Proceed Yet",
                    message:
                        'You need to confirm your order by placing a tick on the confirmation checkbox.',
                })
        }
    },

    setFormProductIndex({ commit }, index) {
        commit(SET_ACTIVE_PRODUCT_INDEX, index)
    },

    async addFormProduct(
        { commit, state, dispatch, rootGetters },
        { product },
    ) {
        const { name, phone } = rootGetters['auth/user']
        const newProduct = {
            ...product,
            site_instructor_name: name,
            // Auto-fill only numbers that can pass the API's mobile-only
            // rule — this field has no input on the web form, so an invalid
            // value would block validation. The API falls back to the
            // builder's phone when the field arrives empty.
            site_instructor_phone: isLikelyAuMobile(phone) ? phone : null,
        }

        commit(ADD_FORM_PRODUCT, newProduct)
        // commit(CLEAR_SELECTED_PRODUCT)

        const { products } = state.jobForm.form
        dispatch('setFormProductIndex', products.length - 1)
    },

    editFormProduct({ commit, dispatch }, { pages, index, step }) {
        const page = pages.show(step)
        commit(PAGE_CHANGED, page)
        commit(CLEAR_FORM_ERRORS)
        commit(SET_EDIT_MODE)
        dispatch('setFormProductIndex', index)
    },

    async removeFormProduct({ commit, state, dispatch }, { index }) {
        if (state.jobForm.form.products.length <= 1) {
            const message = {
                title: 'Are you sure?',
                text: 'There is only one product left in this job. If you remove it, this job will be cancelled automatically. Do you wish to continue?',
                icon: 'warning',
                buttons: true,
                dangerMode: true,
            }

            if (await swal(message)) {
                dispatch('clearForm')
                router.push({ name: 'jobs' })
            }
        } else {
            commit(REMOVE_FORM_PRODUCT, index)
            dispatch(
                'setFormProductIndex',
                state.jobForm.form.products.length - 1,
            )
        }
    },

    addSomethingElse({ commit, state }, { pages, step }) {
        const page = pages.show(step)
        const totalProducts = state.jobForm.form.products.length

        commit(PAGE_CHANGED, page)
        commit(CLEAR_FORM_ERRORS)
        // commit(CLEAR_SELECTED_PRODUCT)

        commit(SET_ACTIVE_PRODUCT_INDEX, totalProducts)
    },

    setFormJobSupplier({ commit }, supplierId) {
        commit(SET_FORM_JOB_SUPPLIER, supplierId)
    },

    clearForm({ commit }) {
        commit(CLEAR_FORM)
    },

    clearFormErrors({ commit }) {
        commit(CLEAR_FORM_ERRORS)
    },

    clearAttachments({ commit }, key) {
        commit(CLEAR_ATTACHMENTS, key)
        commit(CLEAR_FORM_ERRORS)
    },

    updateAttachments({ commit }, files) {
        commit(UPDATE_ATTACHMENTS, files)
    },

    async nextPage({ commit }, payload) {
        const { pages, validation, jumpTo, callback } = payload
        try {
            if (validation instanceof Function) {
                commit(JOB_FORM_REQUESTING, true)
                const err = await validation()
                if (err) {
                    throw new Error("Validation failed");
                }
                commit(JOB_FORM_REQUESTING, false)
            }

            let page = null

            if (jumpTo) {
                page = pages.show(jumpTo)
            } else {
                page = pages.showNext()
            }

            if (callback instanceof Function) await callback(page)

            commit(PAGE_CHANGED, page)
            commit(CLEAR_FORM_ERRORS)
        } catch (e) {
            commit(JOB_FORM_REQUESTING, false)

            if (e.response?.status === 422) {
                commit(SET_FORM_ERRORS, e.response.data.errors)
            }
        }
    },

    async onValidate({ commit }, payload) {
        const { validation, callback } = payload
        try {
            commit(JOB_FORM_REQUESTING, true)
            if (validation instanceof Function) await validation()
            if (callback instanceof Function) await callback()
        } catch (e) {
            commit(JOB_FORM_REQUESTING, false)

            if (e.response?.status === 422) {
                commit(SET_FORM_ERRORS, e.response.data.errors)
            }
        }
    },

    async prevPage(
        { commit, state, dispatch },
        { pages, index, jumpTo, callback },
    ) {
        let page = null

        if (jumpTo) {
            page = pages.show(jumpTo)
        } else {
            page = pages.showPrevious()
        }

        commit(PAGE_CHANGED, page)

        if (page === 'products') {
            if (state.jobForm.form.products.length > 1) {
                commit(REMOVE_FORM_PRODUCT, index)
            } else {
                dispatch('clearForm')
            }
        }

        if (callback instanceof Function) await callback(page)
    },

    inputChanged({ commit }, payload) {
        const { key, value, subkey, subvalue } = payload
        commit(INPUT_CHANGED, { key, value })

        if (subkey) {
            commit(INPUT_CHANGED, { subkey, subvalue })
        }
    },

    checkboxChanged({ commit }, { key, value }) {
        commit(CHECKBOX_CHANGED, { key, value })
    },

    jobRadioChanged({ commit }, { value }) {
        commit(RADIO_CHANGED, value)
    },

    productTabChanged({ commit }, key) {
        commit(PRODUCT_TAB_CHANGED, key)
    },

    readyMixFieldChanged({ commit }, payload) {
        const { key, value, fields, fieldvalue } = payload
        commit(INPUT_CHANGED, { key, value })

        fields.forEach((field) => {
            commit(INPUT_CHANGED, { field, fieldvalue })
        })

        commit(CLEAR_FORM_ERRORS)
    },

    setDragLocation({ commit }, index) {
        commit(SET_DRAGGABLE_FLAG, index)
    },

    setDisplayMapAddress({ commit }, index) {
        commit(SET_DISPLAY_ADDRESS_MAP_FLAG, index)
    },

    setJobForm({ commit }, job) {
        commit(SET_JOB_FORM, job)
    },

    resetState({ commit, state }) {
        if (!state.jobForm.page) commit(RESET_STATE)
    },
}

/**
 * Vuex getters for job module
 * @type {Object}
 */
const getters = {
    jobs: (state) => state.jobs,

    requests: (state) => state.requests,

    pendingRequests: (state) => state.requests.filter((e) => e.pending),

    bidding: (state) => state.jobs.filter((e) => e.status.key === 'bidding'),

    accepted: (state) => state.jobs.filter((e) => e.status.key === 'accepted'),

    request: (state) => state.jobs.filter((e) => e.status.key === 'request'),

    ongoing: (state) => state.jobs.filter((e) => e.status.key === 'ongoing'),

    completed: (state) =>
        state.jobs.filter((e) => e.status.key === 'completed'),

    jobDone: (state) => state.jobs.filter((e) => e.status.key === 'job_done'),

    cancelled: (state) =>
        state.jobs.filter((e) => e.status.key === 'cancelled'),

    denied: (state) => state.jobs.filter((e) => e.status.key === 'denied'),

    jobForm: (state) => state.jobForm,

    recentProduct: ({ jobForm }) => {
        const { products } = jobForm.form
        return products.length
            ? slug(products[jobForm.productIndex].name)
            : null
    },

    site_restrictions: (state, getters, rootState, rootGetters) =>
        rootGetters['settings/default_site_restrictions'],

    logistics: (state, getters, rootState, rootGetters) =>
        rootGetters['settings/default_logistics'],

    truck_sizes: (state, getters, rootState, rootGetters) =>
        rootGetters['settings/default_truck_sizes'],

    transport_methods: (state, getters, rootState, rootGetters) =>
        rootGetters['settings/default_transport_methods'],

    bidpump_phone_number: (state, getters, rootState, rootGetters) =>
        rootGetters['settings/bidpump_phone_number'],

    count: (state) => state.count,

    jobCount: (state) => state.jobCount,

    bidcrete_phone_number: (state) => state.bidcrete_phone_number,

    bidcrete_email_address: (state) => state.bidcrete_email_address,
}

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
}
